import type { Reference, ReferencePlatform, ReferenceType } from "@/types/reference.type";

/**
 * The vocabulary the References surface prints, and nothing else.
 *
 * Every label below is a display form of a value that is really on the wire.
 * There is no label here for a state the API cannot report and no ordering here
 * for a sort the list route does not accept, because a word on the wall is a
 * claim about the library.
 */

export const TYPE_LABEL: Record<ReferenceType, string> = {
  video: "Video",
  image: "Image",
  post: "Post",
  article: "Article",
  /* "note" on the wire is a thought somebody typed rather than a link they
     pasted, and "Idea" is what the composer already calls it. */
  note: "Idea",
  /* A trend saved from Trends, as it is: the trend, not one of its videos. */
  trend: "Trend",
};

/** The order the type control offers, which is the order the plan locked: the
 *  two picture types first, then the two text types, then the typed thought,
 *  then the trends saved from Trends. */
export const TYPE_OPTIONS: ReferenceType[] = ["video", "image", "post", "article", "note", "trend"];

export const PLATFORM_LABEL: Record<ReferencePlatform, string> = {
  tiktok: "TikTok",
  instagram: "Instagram",
  youtube: "YouTube",
  web: "Web",
};

export const PLATFORM_OPTIONS: ReferencePlatform[] = ["tiktok", "instagram", "youtube", "web"];

/**
 * The one mark a tile prints on its picture.
 *
 * Platform where there is one, "Idea" for a typed thought, and the type word as
 * the last resort: `platform` is nullable on anything, so a saved item that came
 * back without one still has to say what it is rather than showing a blank chip.
 */
export function plateMark(reference: Pick<Reference, "type" | "platform">): string {
  if (reference.type === "note") return "Idea";
  /* A trend spans posts and often platforms; its lead clip's platform is not
     what it is. */
  if (reference.type === "trend") return "Trend";
  if (reference.platform) return PLATFORM_LABEL[reference.platform];
  return TYPE_LABEL[reference.type];
}

/**
 * The host a reference was saved from, resolved link first.
 *
 * `resolvedUrl` is the one the backend followed the redirects to and stripped
 * the tracking parameters off, so it is the truthful answer; `sourceUrl` is
 * whatever was pasted. Anything unparseable answers null rather than throwing,
 * because a malformed URL in one row may not take the wall down.
 */
export function referenceDomain(
  reference: Pick<Reference, "resolvedUrl" | "sourceUrl">,
): string | null {
  const raw = reference.resolvedUrl ?? reference.sourceUrl;
  if (!raw) return null;
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** What the picture-less tile prints under its title. The domain if there is
 *  one, and otherwise the same mark the picture tiles carry, so a typed thought
 *  reads "Idea" rather than an empty line. */
export function sourceLabel(
  reference: Pick<Reference, "type" | "platform" | "resolvedUrl" | "sourceUrl">,
): string {
  return referenceDomain(reference) ?? plateMark(reference);
}

/**
 * The tile's mono source line (References / Tile, Meta): where it came from,
 * then what it is. "TIKTOK · VIDEO", "RETAILMENEWS.EXAMPLE · ARTICLE",
 * "INSTAGRAM · CAROUSEL", and a lone "IDEA" for a typed thought.
 */
export function metaLine(
  reference: Pick<Reference, "type" | "platform" | "resolvedUrl" | "sourceUrl" | "carousel" | "videoUrl">,
): string {
  if (reference.type === "note") return TYPE_LABEL.note;
  const kind =
    (reference.carousel?.total ?? 0) > 1
      ? "Carousel"
      : reference.type === "trend"
        ? TYPE_LABEL.trend
        : reference.videoUrl
          ? TYPE_LABEL.video
          : TYPE_LABEL[reference.type];
  const from =
    reference.type === "article" || reference.platform === "web"
      ? referenceDomain(reference)
      : reference.platform
        ? PLATFORM_LABEL[reference.platform]
        : null;
  return from ? `${from} · ${kind}` : kind;
}

/** Where a saved trend opens in Trends, or null when it is not one (or the
 *  trend is gone). */
export function trendPath(reference: Pick<Reference, "type" | "trendId">): string | null {
  return reference.type === "trend" && reference.trendId
    ? `/dashboard/trends?trend=${encodeURIComponent(reference.trendId)}`
    : null;
}

/** The link that opens the thing on its own platform. The resolved one first,
 *  for the same reason as above; the pasted one only when nothing was resolved,
 *  which is the honest fallback rather than showing no way out at all. */
export function openUrl(reference: Pick<Reference, "resolvedUrl" | "sourceUrl">): string | null {
  return reference.resolvedUrl ?? reference.sourceUrl;
}

/** A clip length, from the only duration the wire carries. Printed as a
 *  timestamp because that is what a duration is, never as "11400 ms". */
export function durationLabel(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * What Recreate carries to the Workshop. Same shape as trends' `recreatePrompt`:
 * a composed draft handed through navigation state, never auto-sent.
 *
 * Lives here rather than beside the button so any second surface that offers
 * Recreate on a reference (Home, later) cannot copy a brief that then goes stale.
 */
export function recreatePrompt(
  reference: Pick<Reference, "id" | "title" | "type" | "platform" | "trendId">,
): string {
  const descriptor = [TYPE_LABEL[reference.type], reference.platform && PLATFORM_LABEL[reference.platform]]
    .filter(Boolean)
    .join("; ");
  return [
    `Recreate this reference for our brand: "${reference.title}" (${descriptor}).`,
    `Reference id: ${reference.id} — pull the saved analysis with GetReferenceDetail.`,
    ...(reference.trendId
      ? [`It is a saved trend: trend id ${reference.trendId} — GetTrendDetail shows its live examples.`]
      : []),
    "Study what makes it work, then develop ONE concrete concept idea adapted",
    "to our brand and save it with SaveConceptIdea, citing the reference as a source.",
  ].join("\n");
}
